import { useCallback } from "react";

import { useMockableLocation } from "./use-mockable-location";
import { type QueryParamNavOptions, useMockableNavigate } from "./use-mockable-navigate";

import { useAppDispatch, useAppSelector } from "store";
import { selectQueryString } from "store/selectors/view";
import { viewStateActions } from "store/slices/view";

// This is an alternative to the useSearchParams hook from react-router-dom.
// Use this instead to manage query params.
// useSearchParams has issues with manipulating query params across components.
export const useQueryParamsManager = () => {
  const dispatch = useAppDispatch();
  const queryString = useAppSelector(selectQueryString);
  const getLocation = useMockableLocation();
  const goTo = useMockableNavigate();

  const _update = useCallback(
    (
      action: "set" | "delete",
      params: { [key: string]: string | null },
      options?: QueryParamNavOptions,
    ) => {
      const location = getLocation();
      const newParams = new URLSearchParams(location.search);

      for (const [key, value] of Object.entries(params)) {
        if (action === "set") {
          if (value) {
            newParams.set(key, value);
          } else if (value === null) {
            newParams.delete(key);
          } else {
            // ignore
          }
        } else if (action === "delete") {
          if (value) {
            newParams.delete(key, value);
          } else {
            newParams.delete(key);
          }
        }
      }

      goTo(newParams, location, options);
      dispatch(viewStateActions.updateQueryParams(newParams.toString()));
    },
    [dispatch, getLocation, goTo],
  );

  /**
   * Set query params
   * @param params - An object with the key and value to set
   */
  const setQueryParams = useCallback(
    (params: { [key: string]: string | null }, options?: QueryParamNavOptions) =>
      _update("set", params, options),
    [_update],
  );

  /**
   * Delete query params
   * @param params - An array of strings or arrays with the key and specific value to delete
   */
  const deleteQueryParams = useCallback(
    (params: string[] | [string, string][], options?: QueryParamNavOptions) => {
      const paramsToRemove = params.reduce((acc, key) => {
        if (Array.isArray(key)) {
          return { ...acc, [key[0]]: key[1] };
        }
        return { ...acc, [key]: null };
      }, {});
      _update("delete", paramsToRemove, options);
    },
    [_update],
  );

  /**
   * Get query param
   * @param key - The key of the query param to get
   * @returns The value of the query param
   */
  const getQueryParam = useCallback(
    (key: string) => {
      return new URLSearchParams(queryString).get(key);
    },
    [queryString],
  );

  /**
   * Check if a query param is true
   * @param key - The key of the query param to check
   * @returns True if the query param is a truthy value, false otherwise
   */
  const isQueryParamTruthy = useCallback(
    (key: string) => {
      const queryParams = new URLSearchParams(queryString);
      if (!queryParams.has(key)) {
        return false;
      }
      const value = queryParams.get(key);
      // We allow empty strings to be truthy for when a query param is set with no value (e.g. "?skip-av-check")
      return value === "true" || value === "1" || value === "";
    },
    [queryString],
  );

  return {
    setQueryParams,
    deleteQueryParams,
    getQueryParam,
    isQueryParamTruthy,
  };
};
